import { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { SearchableSelect } from "@/components/SearchableSelect";
import { GraduationCap, Plus, Pencil, Trash2, Save, FileText, ArrowLeft, Link as LinkIcon, Video, Paperclip, X, Loader2, Search, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const emptyContent = () => ({
  categoria_id: "",
  titulo: "",
  subtitulo: "",
  descricao: "",
  conteudo_html: "",
  videos: [],
  arquivos: [],
  faqs: [],
  links_externos: [],
  observacoes: "",
  ordem: 0,
  ativo: true,
});

export default function Training() {
  const { user } = useAuth();
  const isGestao = user?.role === "gestao";
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedCat, setSelectedCat] = useState("");
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null); // full content object
  const [editingCat, setEditingCat] = useState(null); // {id?, nome, ordem, ativo}
  const [editingContent, setEditingContent] = useState(null);
  const [showCatManager, setShowCatManager] = useState(false);

  const load = async () => {
    const [c, i] = await Promise.all([api.get("/training/categories"), api.get("/training/contents")]);
    setCats(c.data || []);
    setItems(i.data || []);
  };
  useEffect(() => { load(); }, []);

  const catMap = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c.nome])), [cats]);
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter(it => {
      if (selectedCat && it.categoria_id !== selectedCat) return false;
      if (!s) return true;
      const hay = `${it.titulo} ${it.subtitulo || ""} ${it.descricao || ""} ${it.conteudo_html || ""}`.toLowerCase();
      return hay.includes(s);
    });
  }, [items, selectedCat, search]);

  const openNewContent = () => setEditingContent(emptyContent());
  const openEditContent = (c) => setEditingContent({ ...emptyContent(), ...c });
  const closeContent = () => setEditingContent(null);
  const closeCat = () => setEditingCat(null);

  const saveContent = async () => {
    if (!editingContent.titulo?.trim() || !editingContent.categoria_id) {
      toast.error("Preencha título e categoria");
      return;
    }
    try {
      if (editingContent.id) {
        await api.put(`/training/contents/${editingContent.id}`, editingContent);
      } else {
        await api.post("/training/contents", editingContent);
      }
      toast.success("Salvo");
      closeContent();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const removeContent = async (c) => {
    if (!window.confirm(`Excluir "${c.titulo}"?`)) return;
    try { await api.delete(`/training/contents/${c.id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const saveCat = async () => {
    if (!editingCat.nome?.trim()) { toast.error("Nome obrigatório"); return; }
    try {
      if (editingCat.id) await api.put(`/training/categories/${editingCat.id}`, editingCat);
      else await api.post("/training/categories", editingCat);
      toast.success("Categoria salva");
      closeCat();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const removeCat = async (c) => {
    if (!window.confirm(`Excluir categoria "${c.nome}"?`)) return;
    try { await api.delete(`/training/categories/${c.id}`); toast.success("Excluída"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  if (viewing) return <Layout><TrainingView content={viewing} catName={catMap[viewing.categoria_id]} onBack={() => setViewing(null)} /></Layout>;

  return (
    <Layout>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><GraduationCap className="w-6 h-6 text-[hsl(var(--primary))]"/>Treinamento</h1>
            <p className="text-xs text-muted-foreground">Central de treinamentos corporativos — {items.length} conteúdo(s)</p>
          </div>
          {isGestao && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowCatManager(true)} data-testid="train-manage-cats"><Pencil className="w-3.5 h-3.5 mr-1"/>Categorias</Button>
              <Button size="sm" onClick={openNewContent} data-testid="train-new-content"><Plus className="w-3.5 h-3.5 mr-1"/>Novo treinamento</Button>
            </div>
          )}
        </div>

        {/* Filtros */}
        <div className="flex gap-2 flex-wrap items-end">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Buscar por palavra-chave</Label>
            <div className="relative mt-1">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <Input data-testid="train-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar..." className="pl-7 h-9" />
            </div>
          </div>
          <div className="w-64">
            <Label className="text-xs">Categoria</Label>
            <SearchableSelect
              value={selectedCat}
              onValueChange={setSelectedCat}
              options={cats.map(c => ({ value: c.id, label: c.nome, hint: c.ativo ? "" : "inativa" }))}
              placeholder="Todas"
              allowClear
              testid="train-filter-cat"
              className="mt-1"
            />
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(c => (
            <Card key={c.id} className="p-3 hover:shadow-md transition-shadow cursor-pointer" data-testid={`train-card-${c.id}`} onClick={() => setViewing(c)}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--primary))] font-semibold">{catMap[c.categoria_id] || "—"}</div>
                  <div className="font-semibold text-sm truncate mt-0.5">{c.titulo}</div>
                  {c.subtitulo && <div className="text-xs text-muted-foreground truncate">{c.subtitulo}</div>}
                </div>
                {!c.ativo && <Badge variant="outline" className="text-[9px] bg-red-50 text-red-600 border-red-200">Inativo</Badge>}
              </div>
              {c.descricao && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{c.descricao}</p>}
              <div className="mt-2 flex gap-2 items-center text-[10px] text-muted-foreground">
                {(c.videos?.length || 0) > 0 && <span className="flex items-center gap-0.5"><Video className="w-3 h-3"/>{c.videos.length}</span>}
                {(c.arquivos?.length || 0) > 0 && <span className="flex items-center gap-0.5"><Paperclip className="w-3 h-3"/>{c.arquivos.length}</span>}
                {(c.faqs?.length || 0) > 0 && <span className="flex items-center gap-0.5"><HelpCircle className="w-3 h-3"/>{c.faqs.length}</span>}
                {(c.links_externos?.length || 0) > 0 && <span className="flex items-center gap-0.5"><LinkIcon className="w-3 h-3"/>{c.links_externos.length}</span>}
              </div>
              {isGestao && (
                <div className="mt-2 flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => openEditContent(c)} data-testid={`train-edit-${c.id}`}><Pencil className="w-3 h-3 mr-1"/>Editar</Button>
                  <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-600" onClick={() => removeContent(c)} data-testid={`train-delete-${c.id}`}><Trash2 className="w-3 h-3"/></Button>
                </div>
              )}
            </Card>
          ))}
          {filtered.length === 0 && (
            <Card className="p-6 text-center text-muted-foreground col-span-full">
              {items.length === 0 ? "Nenhum treinamento cadastrado ainda." : "Nenhum resultado com esses filtros."}
            </Card>
          )}
        </div>
      </div>

      {/* Modal categorias */}
      <Dialog open={showCatManager} onOpenChange={setShowCatManager}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Categorias de treinamento</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {cats.map(c => (
              <div key={c.id} className="flex items-center gap-2 p-2 border border-border rounded-sm">
                <div className="flex-1">
                  <div className="font-semibold text-sm">{c.nome}</div>
                  <div className="text-[10px] text-muted-foreground">Ordem {c.ordem} · {c.ativo ? "Ativa" : "Inativa"}</div>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setEditingCat({ ...c })} data-testid={`cat-edit-${c.id}`}><Pencil className="w-3 h-3"/></Button>
                <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-600" onClick={() => removeCat(c)} data-testid={`cat-delete-${c.id}`}><Trash2 className="w-3 h-3"/></Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCat({ nome: "", ordem: cats.length * 10, ativo: true })} data-testid="cat-new"><Plus className="w-3.5 h-3.5 mr-1"/>Nova categoria</Button>
            <Button onClick={() => setShowCatManager(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal edit categoria */}
      {editingCat && (
        <Dialog open onOpenChange={closeCat}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{editingCat.id ? "Editar categoria" : "Nova categoria"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Nome *</Label><Input data-testid="cat-nome" value={editingCat.nome} onChange={(e) => setEditingCat({ ...editingCat, nome: e.target.value })} className="mt-1"/></div>
              <div><Label className="text-xs">Ordem</Label><Input type="number" data-testid="cat-ordem" value={editingCat.ordem} onChange={(e) => setEditingCat({ ...editingCat, ordem: Number(e.target.value) })} className="mt-1"/></div>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><Switch checked={editingCat.ativo} onCheckedChange={(v) => setEditingCat({ ...editingCat, ativo: v })} data-testid="cat-ativo"/>Ativa</label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeCat}>Cancelar</Button>
              <Button onClick={saveCat} data-testid="cat-save"><Save className="w-3.5 h-3.5 mr-1"/>Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal edit conteúdo */}
      {editingContent && (
        <TrainingEditor
          value={editingContent}
          onChange={setEditingContent}
          categories={cats}
          onClose={closeContent}
          onSave={saveContent}
        />
      )}
    </Layout>
  );
}

function TrainingEditor({ value, onChange, categories, onClose, onSave }) {
  const patch = (k, v) => onChange({ ...value, [k]: v });
  const [busyFile, setBusyFile] = useState(false);
  const upload = async (file) => {
    if (!file) return null;
    if (file.size > 200 * 1024 * 1024) { toast.error("Arquivo > 200MB"); return null; }
    const fd = new FormData(); fd.append("file", file);
    const r = await fetch(`${API}/files/upload`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, credentials: "include", body: fd });
    if (!r.ok) throw new Error("upload");
    return await r.json();
  };
  const addFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusyFile(true);
    try { const d = await upload(f); patch("arquivos", [...(value.arquivos || []), { id: d.id, filename: f.name }]); }
    catch { toast.error("Falha upload"); }
    finally { setBusyFile(false); e.target.value = ""; }
  };
  const addVideoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusyFile(true);
    try { const d = await upload(f); patch("videos", [...(value.videos || []), { kind: "upload", id: d.id, url: `${API}/files/${d.id}/download`, filename: f.name }]); }
    catch { toast.error("Falha upload"); }
    finally { setBusyFile(false); e.target.value = ""; }
  };
  const addVideoLink = () => {
    const url = window.prompt("URL do vídeo (YouTube, Vimeo etc):");
    if (!url) return;
    patch("videos", [...(value.videos || []), { kind: "link", url, filename: url.slice(0, 60) }]);
  };
  const addFaq = () => patch("faqs", [...(value.faqs || []), { titulo: "", resposta: "", ordem: (value.faqs?.length || 0) * 10 }]);
  const setFaq = (i, k, v) => { const nx = [...(value.faqs || [])]; nx[i] = { ...nx[i], [k]: v }; patch("faqs", nx); };
  const rmFaq = (i) => patch("faqs", (value.faqs || []).filter((_, ix) => ix !== i));
  const addLink = () => patch("links_externos", [...(value.links_externos || []), { titulo: "", url: "", observacao: "" }]);
  const setLink = (i, k, v) => { const nx = [...(value.links_externos || [])]; nx[i] = { ...nx[i], [k]: v }; patch("links_externos", nx); };
  const rmLink = (i) => patch("links_externos", (value.links_externos || []).filter((_, ix) => ix !== i));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{value.id ? "Editar treinamento" : "Novo treinamento"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Categoria *</Label>
              <SearchableSelect
                value={value.categoria_id}
                onValueChange={(v) => patch("categoria_id", v)}
                options={categories.map(c => ({ value: c.id, label: c.nome }))}
                placeholder="Selecionar..." searchPlaceholder="Buscar..."
                testid="train-cat-select" className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Ordem</Label>
              <Input type="number" value={value.ordem || 0} onChange={(e) => patch("ordem", Number(e.target.value))} className="mt-1"/>
            </div>
          </div>
          <div><Label className="text-xs">Título *</Label><Input data-testid="train-titulo" value={value.titulo} onChange={(e) => patch("titulo", e.target.value)} className="mt-1"/></div>
          <div><Label className="text-xs">Subtítulo</Label><Input data-testid="train-subtitulo" value={value.subtitulo} onChange={(e) => patch("subtitulo", e.target.value)} className="mt-1"/></div>
          <div><Label className="text-xs">Descrição breve</Label><Textarea rows={2} value={value.descricao} onChange={(e) => patch("descricao", e.target.value)} className="mt-1"/></div>
          <div>
            <Label className="text-xs">Conteúdo (HTML — aceita tags &lt;b&gt;, &lt;i&gt;, &lt;u&gt;, &lt;ul&gt;, &lt;a&gt;, &lt;table&gt;, &lt;img&gt; etc.)</Label>
            <Textarea data-testid="train-html" rows={8} value={value.conteudo_html} onChange={(e) => patch("conteudo_html", e.target.value)} className="mt-1 font-mono text-[11px]"/>
          </div>

          {/* Vídeos */}
          <div className="border border-border rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider">Vídeos</Label>
              <div className="flex gap-1">
                <label className="cursor-pointer text-[10px] px-2 py-1 border border-border rounded-sm hover:bg-secondary/30">
                  <input type="file" accept="video/*" hidden onChange={addVideoUpload} data-testid="train-video-upload"/>Upload
                </label>
                <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={addVideoLink} data-testid="train-video-link"><LinkIcon className="w-3 h-3 mr-1"/>Link</Button>
              </div>
            </div>
            {(value.videos || []).map((v, i) => (
              <div key={i} className="flex items-center gap-2 text-xs bg-secondary/40 p-1.5 rounded-sm">
                <Video className="w-3.5 h-3.5 text-[hsl(var(--primary))]"/>
                <span className="flex-1 truncate">{v.filename || v.url}</span>
                <span className="text-[9px] text-muted-foreground">{v.kind}</span>
                <button onClick={() => patch("videos", value.videos.filter((_, ix) => ix !== i))} className="text-red-600"><X className="w-3 h-3"/></button>
              </div>
            ))}
          </div>

          {/* Arquivos */}
          <div className="border border-border rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider">Arquivos anexos</Label>
              <label className="cursor-pointer text-[10px] px-2 py-1 border border-border rounded-sm hover:bg-secondary/30">
                <input type="file" hidden onChange={addFile} data-testid="train-file-upload"/>{busyFile ? <Loader2 className="w-3 h-3 inline animate-spin"/> : "Anexar"}
              </label>
            </div>
            {(value.arquivos || []).map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs bg-secondary/40 p-1.5 rounded-sm">
                <FileText className="w-3.5 h-3.5"/><span className="flex-1 truncate">{f.filename}</span>
                <button onClick={() => patch("arquivos", value.arquivos.filter((_, ix) => ix !== i))} className="text-red-600"><X className="w-3 h-3"/></button>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="border border-border rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider">Perguntas e respostas</Label>
              <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={addFaq} data-testid="train-faq-add"><Plus className="w-3 h-3 mr-1"/>Adicionar</Button>
            </div>
            {(value.faqs || []).map((q, i) => (
              <div key={i} className="p-2 border border-border rounded-sm space-y-1">
                <div className="flex gap-1">
                  <Input placeholder="Título da pergunta" value={q.titulo} onChange={(e) => setFaq(i, "titulo", e.target.value)} className="text-xs h-8"/>
                  <Input type="number" placeholder="ordem" value={q.ordem || 0} onChange={(e) => setFaq(i, "ordem", Number(e.target.value))} className="w-16 text-xs h-8"/>
                  <button onClick={() => rmFaq(i)} className="text-red-600 px-1"><X className="w-3 h-3"/></button>
                </div>
                <Textarea placeholder="Resposta" rows={2} value={q.resposta} onChange={(e) => setFaq(i, "resposta", e.target.value)} className="text-xs"/>
              </div>
            ))}
          </div>

          {/* Links externos */}
          <div className="border border-border rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider">Links externos / recursos</Label>
              <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={addLink} data-testid="train-link-add"><Plus className="w-3 h-3 mr-1"/>Adicionar</Button>
            </div>
            {(value.links_externos || []).map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-1">
                <Input placeholder="Título" value={l.titulo} onChange={(e) => setLink(i, "titulo", e.target.value)} className="col-span-4 text-xs h-8"/>
                <Input placeholder="URL" value={l.url} onChange={(e) => setLink(i, "url", e.target.value)} className="col-span-4 text-xs h-8"/>
                <Input placeholder="Observação" value={l.observacao || ""} onChange={(e) => setLink(i, "observacao", e.target.value)} className="col-span-3 text-xs h-8"/>
                <button onClick={() => rmLink(i)} className="col-span-1 text-red-600"><X className="w-3 h-3"/></button>
              </div>
            ))}
          </div>

          <div><Label className="text-xs">Observações internas</Label><Textarea rows={2} value={value.observacoes} onChange={(e) => patch("observacoes", e.target.value)} className="mt-1"/></div>
          <label className="flex items-center gap-2 text-xs cursor-pointer"><Switch checked={value.ativo} onCheckedChange={(v) => patch("ativo", v)} data-testid="train-ativo"/>Publicado (visível a todos os usuários)</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={onSave} data-testid="train-save"><Save className="w-4 h-4 mr-1"/>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrainingView({ content, catName, onBack }) {
  return (
    <div className="p-4 max-w-4xl mx-auto">
      <Button variant="outline" size="sm" onClick={onBack} className="mb-3" data-testid="train-back"><ArrowLeft className="w-3.5 h-3.5 mr-1"/>Voltar</Button>
      <div className="text-xs uppercase tracking-wider text-[hsl(var(--primary))] font-semibold">{catName || "—"}</div>
      <h1 className="text-2xl font-bold mt-1">{content.titulo}</h1>
      {content.subtitulo && <p className="text-sm text-muted-foreground italic mt-1">{content.subtitulo}</p>}
      {content.descricao && <p className="text-sm mt-3 whitespace-pre-wrap">{content.descricao}</p>}

      {content.conteudo_html && (
        <div className="mt-6 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: content.conteudo_html }} />
      )}

      {content.videos?.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[hsl(var(--primary))] mb-2 flex items-center gap-1.5"><Video className="w-4 h-4"/>Vídeos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {content.videos.map((v, i) => (
              <div key={i} className="rounded-sm overflow-hidden border border-border bg-black">
                {v.kind === "upload" ? (
                  <video controls src={v.url} className="w-full max-h-96" data-testid={`train-video-${i}`}/>
                ) : (
                  <a href={v.url} target="_blank" rel="noreferrer" className="p-3 text-white text-sm flex items-center gap-2 bg-black hover:bg-gray-900"><LinkIcon className="w-4 h-4"/>{v.filename || v.url}</a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {content.arquivos?.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[hsl(var(--primary))] mb-2 flex items-center gap-1.5"><Paperclip className="w-4 h-4"/>Arquivos e materiais</h2>
          <ul className="space-y-1">
            {content.arquivos.map((f, i) => (
              <li key={i}>
                <a href={`${API}/files/${f.id}/download`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline p-2 border border-border rounded-sm bg-card" data-testid={`train-file-${i}`}>
                  <FileText className="w-4 h-4"/>{f.filename}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {content.faqs?.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[hsl(var(--primary))] mb-2 flex items-center gap-1.5"><HelpCircle className="w-4 h-4"/>Perguntas frequentes</h2>
          <div className="space-y-2">
            {[...content.faqs].sort((a, b) => (a.ordem || 0) - (b.ordem || 0)).map((q, i) => (
              <details key={i} className="p-3 border border-border rounded-sm bg-card group">
                <summary className="cursor-pointer font-semibold text-sm">{q.titulo || `Pergunta ${i + 1}`}</summary>
                <div className="mt-2 text-sm whitespace-pre-wrap text-muted-foreground">{q.resposta}</div>
              </details>
            ))}
          </div>
        </section>
      )}

      {content.links_externos?.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[hsl(var(--primary))] mb-2 flex items-center gap-1.5"><LinkIcon className="w-4 h-4"/>Links externos e recursos</h2>
          <ul className="space-y-1">
            {content.links_externos.map((l, i) => (
              <li key={i} className="p-2 border border-border rounded-sm bg-card">
                <a href={l.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline font-semibold">{l.titulo || l.url}</a>
                {l.observacao && <div className="text-xs text-muted-foreground mt-0.5">{l.observacao}</div>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {content.observacoes && (
        <section className="mt-6 p-3 border border-yellow-300 bg-yellow-50 rounded-sm text-xs whitespace-pre-wrap">
          <div className="font-semibold mb-1">Observações importantes</div>
          {content.observacoes}
        </section>
      )}
    </div>
  );
}
